import React from 'react'
import { observable, action, makeObservable } from 'mobx'
import i18n from 'i18next'
import * as R from 'ramda'
import { v4 as uuidv4 } from 'uuid'
import NewWidget from './models/NewWidget'
import Select from '../../../form/Select'
import StagedForm from '../StagedForm'
import ChartPreviewContainer from '../ChartPreviewContainer'
import Checkbox from '../../../form/Checkbox'
import CheckboxGroup from '../../../form/CheckboxGroup'
import {
    getIndustries, getProductTypes, getSurveyTypes, getRespondentTypes,
} from '../../../../utils/helpers'
import { getQuestionTitle } from '../../../../utils/QuestionHelper'
import { stripOutHtml, unescapeHtmlTags, hasRequiredDependencies } from '../../../helpers'

class WidgetCreatorStore {
    constructor(dashStore) {
        makeObservable(this)
        this.appStore = dashStore.appStore
        this.dashStore = dashStore
        this.newWidget = new NewWidget(undefined, undefined, dashStore.appStore)
    }

    @observable loading = false

    @observable loadingQuestions = false

    @observable error = false

    @observable currentStage = 0

    @observable show = false

    @observable questions = []

    @observable detailedQuestions = []

    @observable newWidget = new NewWidget()

    @observable portfolios = []

    @observable divisions = []

    @observable widgetType = 'single'

    @observable showWidgetType = false

    @observable showWidgetCreator = false

    @observable multiQuestions = new Map()

    @observable multiDetailedQuestions = new Map()

    @observable fullSurvey = {}

    @observable registrationDataQuestions = []

    @observable showWidgetCreatorV3 = false

    @observable openDataLevelAccordian = false

    detailedQuestionsMap = new Map()

    benchmarkTags = []

    kpis = []

    displayAsOptions = {
        average: { label: 'Average', value: 'avg' },
        averageNF: { label: 'Average', value: 'NFOption' },
        pct: { label: 'Response Percentage', value: 'pct' },
        byRow: { label: 'By Option', value: 'pct' },
        nps: { label: 'Net Promoter Score', value: 'nps' },
        total: { label: 'Total', value: 'total' },
    }

    productMap = {}

    questionDisplayAs = {
        RD: {
            numeric: ['average', 'pct'].map((d) => this.displayAsOptions[d]),
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
            nps: ['pct', 'nps'].map((d) => this.displayAsOptions[d]),
        },
        CH: {
            numeric: ['pct'].map((d) => this.displayAsOptions[d]),
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        TB: {
            numeric: ['average', 'pct'].map((d) => this.displayAsOptions[d]),
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        'TB - row': {
            numeric: ['average', 'pct'].map((d) => this.displayAsOptions[d]),
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        SB: {
            numeric: ['average', 'pct'].map((d) => this.displayAsOptions[d]),
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        OD: {
            numeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        'OD - row': {
            numeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        SG: {
            numeric: ['average'].map((d) => this.displayAsOptions[d]),
            numericEvent: ['average', 'pct'].map((d) => this.displayAsOptions[d]),
        },
        NF: {
            numeric: ['average', 'byRow'].map((d) => this.displayAsOptions[d]),
        },
        'NF - row': {
            numeric: ['average'].map((d) => this.displayAsOptions[d]),
        },
        I: {
            numeric: ['average'].map((d) => this.displayAsOptions[d]),
        },
        PC: {
            numeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        TX: {
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
        TA: {
            nonNumeric: ['pct'].map((d) => this.displayAsOptions[d]),
        },
    }

    blockedQuestions = ['TX', 'TA', 'TL', 'RT', 'CO', 'UV']

    blockedEventLevelQuestions = ['TL', 'RT', 'CO', 'UV']

    dataLevelOptions = {
        3: { value: 'client', label: 'Company' },
        4: { value: 'division', label: 'Division' },
        5: { value: 'portfolio', label: 'Portfolio' },
        6: { value: 'product', label: 'Event' },
    }

    dataLevels = []

    timePeriodOptions = {
        client: [
            { value: 'YOY', label: 'Year On Year' },
            { value: 'ROL-36', label: 'Last 36 Months' },
            { value: 'ROL-84', label: 'Long-term' },
        ],
        division: [
            { value: 'YOY', label: 'Year On Year' },
            { value: 'ROL-36', label: 'Last 36 Months' },
            { value: 'ROL-84', label: 'Long-term' },
        ],
        portfolio: [
            { value: 'YOY', label: 'Year On Year' },
            { value: 'ROL-36', label: 'Last 36 Months' },
            { value: 'ROL-84', label: 'Long-term' },
        ],
        product: [
            { value: 'YOY', label: 'Year On Year' },
            { value: 'last-3', label: 'Last 3 Months' },
            { value: 'last-6', label: 'Last 6 Months' },
            { value: 'last-12', label: 'Last 12 Months' },
            { value: 'last-24', label: 'Last 24 Months' },
        ],
    }

    questionChartOptions = {
        quadrantAnalysis: {
            currentLevel: ['quadrantAnalysis'],
            levelBelow: ['quadrantAnalysis'],
        },
        KPI: {
            currentLevel: [
                'gauge', 'gauge0-5', 'column', 'bar', 'stackedColumn', 'stackedBar', 'donut', 'pie', 'treemap', 'text',
            ],
            levelBelow: ['column', 'bar', 'stackedColumn', 'stackedBar'],
        },
        multiQuestion: {
            currentLevel: ['column', 'bar', 'stackedColumn', 'stackedBar', 'multiText'],
            levelBelow: ['column', 'bar', 'stackedColumn', 'stackedBar', 'line'],
        },
        LMX: {
            currentLevel: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap', 'lmx'],
            levelBelow: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
        },
        RD: {
            numeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                    avg: ['gauge', 'gauge0-5', 'column', 'bar', 'text'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                    avg: ['column', 'bar', 'line'],
                },
            },
            nonNumeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
            },
            nps: {
                currentLevel: ['gauge', 'gauge0-5', 'column', 'bar', 'text'],
                levelBelow: ['column', 'bar', 'line'],
            },
        },
        CH: {
            numeric: {
                currentLevel: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
            },
            nonNumeric: {
                currentLevel: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
            },
        },
        TB: {
            numeric: {
                currentLevel: {
                    pct: ['column', 'bar', 'line'],
                    avg: ['column', 'bar', 'line', 'radar'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'line'],
                    avg: ['column', 'bar', 'line', 'radar'],
                },
            },
            nonNumeric: {
                currentLevel: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
            },
        },
        'TB - row': {
            numeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                    avg: ['gauge', 'gauge0-5', 'column', 'bar', 'text'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                    avg: ['column', 'bar', 'line'],
                },
            },
            nonNumeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
            },
        },
        SB: {
            numeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                    avg: ['gauge', 'gauge0-5', 'column', 'bar', 'text'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                    avg: ['column', 'bar', 'line'],
                },
            },
            nonNumeric: {
                currentLevel: {
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                },
                levelBelow: {
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
            },
        },
        OD: {
            numeric: {
                currentLevel: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                levelBelow: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
            },
        },
        'OD - row': {
            numeric: {
                currentLevel: ['gauge', 'gauge0-5', 'column', 'bar', 'line', 'text'],
                levelBelow: ['column', 'bar', 'line'],
            },
        },
        SG: {
            numeric: {
                currentLevel: ['gauge', 'gauge0-5', 'column', 'bar', 'line', 'text'],
                levelBelow: ['column', 'bar', 'line'],
            },
        },
        NF: {
            numeric: {
                currentLevel: {
                    avg: ['column', 'bar', 'line', 'text'],
                    pct: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line', 'treemap'],
                },
                levelBelow: {
                    avg: ['column', 'bar', 'line'],
                    pct: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                },
            },
        },
        'NF - row': {
            numeric: {
                currentLevel: {
                    avg: ['column', 'bar', 'line', 'text', 'treemap'],
                },
                levelBelow: {
                    avg: ['column', 'bar', 'line'],
                },
            },
        },
        I: {
            numeric: {
                currentLevel: ['column', 'bar', 'line', 'text'],
                levelBelow: ['column', 'bar', 'line'],
            },
        },
        PC: {
            numeric: {
                currentLevel: ['donut', 'pie', 'column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
                levelBelow: ['column', 'bar', 'stackedBar', 'stackedColumn', 'line'],
            },
        },
        TX: {
            nonNumeric: {
                currentLevel: ['textResponse', 'wordCloud'],
                levelBelow: ['textResponse', 'wordCloud'],
            },
        },
        TA: {
            nonNumeric: {
                currentLevel: ['textResponse', 'wordCloud'],
                levelBelow: ['textResponse', 'wordCloud'],
            },
        },

    }

    chartTypes = {
        gauge: {
            label: 'Gauge',
            value: 'gauge',
            src: 'fa-regular fa-gauge',
            isIcon: true,
            iconLeft: true,
        },
        'gauge0-5': {
            label: 'Gauge 0-5',
            value: 'gauge0-5',
            src: 'fa-regular fa-gauge',
            isIcon: true,
            iconLeft: true,
        },
        column: {
            label: 'Column',
            value: 'column',
            src: 'fa-sharp fa-regular fa-chart-bar',
            isIcon: true,
            iconLeft: true,
        },
        bar: {
            label: 'Bar',
            value: 'bar',
            src: 'fa-sharp fa-light fa-chart-column',
            isIcon: true,
            iconLeft: true,
        },
        stackedColumn: {
            label: 'Stacked Column',
            value: 'stackedColumn',
            src: 'fa-sharp fa-thin fa-chart-column',
            isIcon: true,
            iconLeft: true,
        },
        stackedBar: {
            label: 'Stacked Bar',
            value: 'stackedBar',
            src: 'fa-sharp fa-thin fa-chart-bar',
            isIcon: true,
            iconLeft: true,
        },
        donut: {
            label: 'Donut',
            value: 'donut',
            src: 'fa-light fa-donut',
            isIcon: true,
            iconLeft: true,
        },
        pie: {
            label: 'Pie',
            value: 'pie',
            src: 'fa-solid fa-chart-pie',
            isIcon: true,
            iconLeft: true,
        },
        treemap: {
            label: 'Treemap',
            value: 'treemap',
            src: 'fa-solid fa-chart-tree-map',
            isIcon: true,
            iconLeft: true,
        },
        text: {
            label: 'Text',
            value: 'text',
            src: 'fa-sharp-duotone fa-thin fa-align-center',
            isIcon: true,
            iconLeft: true,
        },
        textResponse: {
            label: 'Text Response',
            value: 'textResponse',
            src: 'fa-light fa-align-left',
            isIcon: true,
            iconLeft: true,
        },
        wordCloud: {
            label: 'Word Cloud',
            value: 'wordCloud',
            src: 'fa-regular fa-cloud-word',
            isIcon: true,
            iconLeft: true,
        },
        line: {
            label: 'Line',
            value: 'line',
            src: 'fa-solid fa-chart-line',
            isIcon: true,
            iconLeft: true,
        },
        radar: {
            label: 'Radar',
            value: 'radar',
            src: 'fa-sharp-duotone fa-thin fa-chart-radar"',
            isIcon: true,
            iconLeft: true,
        },
        multiText: {
            label: 'Multi-Text',
            value: 'multiText',
            src: 'fa-sharp fa-light fa-line-columns',
            isIcon: true,
            iconLeft: true,
        },
        lmx: {
            label: 'Matrix',
            value: 'lmx',
            src: 'fa-light fa-chart-scatter-3d',
            isIcon: true,
            iconLeft: true,
        },
        quadrantAnalysis: {
            label: 'Bubble chart',
            value: 'quadrantAnalysis',
            src: 'fa-regular fa-chart-scatter',
            isIcon: true,
            iconLeft: true,
        },
    }

    filterTree = []

    surveyTypes = []

    getDivisions = async () => {
        const { appStore } = this
        let divisions = []

        try {
            divisions = await appStore.http.get('/division')
        } catch (e) {
            console.error(e)
        }

        return divisions
    }

    init = async () => {
        const { appStore } = this
        const { oId } = this.dashStore
        this.loading = true
        this.productMap = await this.getProdMap()
        await this.getBenchmarkTags()
        await this.getNonQuestionData()
        const isMultiDivision = R.path(['userData', 'flags', 'isMultiDivision'], appStore)
        const promises = [appStore.http.get('/portfolio'), appStore.http.get('/product')]
        if (isMultiDivision) promises.push(this.getDivisions())
        const data = await Promise.all(promises)
        if (isMultiDivision) this.divisions = data[2]

        const possibleDataLevels = isMultiDivision ? this.dataLevelOptions : {
            3: { value: 'client', label: 'Company' },
            5: { value: 'portfolio', label: 'Portfolio' },
            6: { value: 'product', label: 'Event' },
        }

        this.dataLevels = Object.keys(possibleDataLevels).reduce((dl, l) => {
            if (l === 4 && !isMultiDivision) return dl
            if (l === 5 && this.portfolios.length < 2) return dl
            if (l < oId) return dl
            return [...dl, possibleDataLevels[l]]
        }, [])
        this.loading = false
    }

    initDataSetup = async () => {
        const { appStore, dashStore } = this
        const { oId } = dashStore
        this.loading = true
        await this.getBenchmarkTags()
        const isMultiDivision = R.path(['userData', 'flags', 'isMultiDivision'], appStore)
        const promises = [appStore.http.get('/portfolio'), appStore.http.get('/product')]
        if (isMultiDivision) promises.push(appStore.http.get('/division'))
        const data = await Promise.all(promises)
        this.portfolios = data[0]
        this.products = data[1]
        if (isMultiDivision) this.divisions = data[2]

        const possibleDataLevels = isMultiDivision ? this.dataLevelOptions : {
            3: { value: 'client', label: 'Company' },
            5: { value: 'portfolio', label: 'Portfolio' },
            6: { value: 'product', label: 'Event' },
        }

        this.dataLevels = Object.keys(possibleDataLevels).reduce((dl, l) => {
            if (l === 4 && !isMultiDivision) return dl
            if (l === 5 && this.portfolios.length < 2) return dl
            if (l < oId) return dl
            return [...dl, possibleDataLevels[l]]
        }, [])
        this.loading = false
    }

    setSurveyTypes = () => {
        const { oId, surveys } = this.dashStore
        if (oId === 6) {
            this.surveyTypes = surveys.reduce((types, s) => {
                if (s.surveyType && !types.includes(s.surveyType)) types.push(s.surveyType)
                return types
            }, [])
        }
    }

    createTreeData = (level, filters) => {
        const { dashStore } = this
        this.filterTree = dashStore.createTreeData(level, filters)
    }

    getMultiEventLevelQuesions = async (multiIndex, quadrantIndex) => {
        const { appStore, newWidget } = this
        const metricPath = this.widgetType === 'quadrantAnalysis'
            ? ['quadrant', quadrantIndex[multiIndex]]
            : ['multiQuestion', multiIndex]
        const respondentType = R.pathOr(newWidget.respondentType, [...metricPath, 'respondentType'], newWidget)
        const surveyType = R.pathOr(newWidget.surveyType, [...metricPath, 'surveyType'], newWidget)
        const industry = R.pathOr(newWidget.industry, [...metricPath, 'industry'], newWidget)
        const { langcode } = appStore
        const { recordId, surveys } = this.dashStore

        const surveyBasic = surveys
            .filter((s) => (
                s.productId === recordId
                && s.respondentType === respondentType
                && s.surveyType === surveyType
                && s.industry === industry
            ))
            .reduce((s, cs) => (cs.year > s.year ? cs : s), { year: 0 })
        if (!surveyBasic || !surveyBasic.surveyId) {
            this.detailedQuestions = []
            return
        }
        const surveyDetailed = await this.getSurveyQuestions(surveyBasic.surveyId)

        this.fullSurvey = surveyDetailed

        const surveyQuestions = surveyDetailed.survey.pages
            .reduce((q, p) => {
                q.push(p.questions)
                return q
            }, []).flat(2).filter((q) => !!q)

        this.multiQuestions.set(multiIndex, await Promise.all(
            surveyQuestions.map((q) => this.getQuestion(q.questionId, true)),
        ))

        this.questions = this.questions
            .filter(({ question }) => !this.blockedEventLevelQuestions.includes(question.questionTypeCode))

        const { metadata } = surveyDetailed.survey
        const { productInfo } = metadata
        const { industrySpecificInfo } = productInfo
        const { productType } = industrySpecificInfo
        const group = this.getRespondentTypeName(industry, productType, surveyType, respondentType)

        this.multiDetailedQuestions.set(multiIndex, this.formatQuestions(
            this.multiQuestions.get(multiIndex),
            langcode,
            group,
            respondentType,
        ))
    }

    getMultiQuestions = async (multiIndex, quadrantIndex) => {
        const { appStore, newWidget, widgetType } = this
        const { langcode } = appStore
        const { oId, recordId } = this.dashStore
        const { clientId } = appStore.userData
        this.loadingQuestions = true

        const metricPath = widgetType === 'quadrantAnalysis'
            ? ['quadrant', quadrantIndex[multiIndex]]
            : ['multiQuestion', multiIndex]

        const industry = R.pathOr(newWidget.industry, [...metricPath, 'industry'], newWidget)
        const respondentType = R.pathOr(newWidget.respondentType, [...metricPath, 'respondentType'], newWidget)
        const surveyType = R.pathOr(newWidget.surveyType, [...metricPath, 'surveyType'], newWidget)
        const productType = R.pathOr(newWidget.industry, [...metricPath, 'productType'], newWidget)

        if (oId === 6) {
            await this.getMultiEventLevelQuesions(multiIndex, quadrantIndex)
            this.loadingQuestions = false
            return
        }

        const promiseParamsArray = [
            [3, [[clientId, 3]]],
            [4, [[recordId, 4]]],
            [5, [[recordId, 5]]],
        ]

        const questions = await Promise.all(
            promiseParamsArray.map((p) => {
                if (p[0] === oId) {
                    if (widgetType === 'quadrantAnalysis') {
                        const respondentTypes = Object.values(R.pathOr(
                            {},
                            [
                                'productMap',
                                industry,
                                'children',
                                productType,
                                'children',
                                surveyType,
                                'children',
                            ],
                            this,
                        ))
                        return p[1].map((p1) => (
                            respondentTypes.map((rt) => this.getQlistQuestions(p1[0], p1[1], rt.value, surveyType, productType))
                        ))
                    }
                    return p[1].map((p1) => this.getQlistQuestions(p1[0], p1[1], respondentType, surveyType, productType))
                }
                return undefined
            }).flat(2),
        )

        this.multiQuestions.set(multiIndex, questions.flat(2).filter((q) => (
            !!q && !this.blockedQuestions.includes(q.question.questionTypeCode)
        )))

        this.multiDetailedQuestions.set(multiIndex, this.formatQuestions(this.multiQuestions.get(multiIndex), langcode))
        this.loadingQuestions = false
    }

    getQuestions = async (metric, forQuadrant = false) => {
        const { appStore, newWidget } = this
        const { langcode } = appStore
        const { oId, recordId } = this.dashStore
        const { clientId } = appStore.userData

        const productType = R.pathOr(metric.productType, ['productType', 'value'], metric)
        const surveyType = R.pathOr(metric.surveyType, ['surveyType', 'value'], metric)
        const industry = R.pathOr(metric.industry, ['industry', 'value'], metric)
        const respondentType = R.pathOr(metric.respondentType, ['respondentType', 'value'], metric)
        this.loadingQuestions = true
        if (oId === 6) {
            await this.getEventLevelQuesions(metric)
            this.loadingQuestions = false
            return
        }

        const promiseParamsArray = [
            [3, [[clientId, 3]]],
            [4, [[recordId, 4]]],
            [5, [[recordId, 5]]],
        ]

        const questions = await Promise.all(
            promiseParamsArray.map((p) => {
                if (p[0] === oId) {
                    if (forQuadrant) {
                        const respondentTypes = Object.values(R.pathOr(
                            {},
                            [
                                'productMap',
                                industry,
                                'children',
                                productType,
                                'children',
                                surveyType,
                                'children',
                            ],
                            this,
                        ))
                        return p[1].map((p1) => (
                            respondentTypes.map((rt) => this.getQlistQuestions(metric, p1[0], p1[1], rt.value))
                        ))
                    }
                    return p[1].map((p1) => this.getQlistQuestions(metric, p1[0], p1[1]))
                }
                return undefined
            }).flat(2),
        )

        this.questions = questions.flat(2).filter((q) => (
            !!q && !this.blockedQuestions.includes(q.question.questionTypeCode)
        ))

        this.detailedQuestions = this.formatQuestions(this.questions, langcode)
        this.detailedQuestionsMap.set(`${industry}-${productType}-${surveyType}-${respondentType}-false`, this.formatQuestions(this.questions, langcode))
        this.loadingQuestions = false
    }

    getQuestionLabel = (question, langcode, optionContent, includeQuestion = true) => {
        const initialLocale = R.pathOr('en_GB', ['metadata', 'initialLocale'], question)
        const questionContent = R.pathOr({}, ['content', 'shortTitle'], question)
        let optionLabel = ''
        if (optionContent) {
            optionLabel = R.pathOr('', ['text'], getQuestionTitle(
                {
                    content: optionContent,
                    locale: langcode,
                    defaultLocale: initialLocale,
                    initialLocale: 'en_GB',
                },
            ))
        }

        if (!includeQuestion) return optionLabel

        return `${R.pathOr('', ['text'], getQuestionTitle(
            {
                content: questionContent,
                locale: langcode,
                defaultLocale: initialLocale,
                initialLocale: 'en_GB',
            },
        ))}${optionContent ? ` - ${optionLabel}` : ''}`
    }

    formatQuestions = (questions, langcode, group, respondentType) => (
        questions
            .filter((q) => !!q)
            .map(({ question, parentId }) => {
                const questionLabel = this.getQuestionLabel(question, langcode)
                if (!['FM', 'NF', 'OD', 'TB'].includes(question.questionTypeCode)) {
                    return ({
                        ...question,
                        group,
                        respondentType,
                        label: questionLabel,
                        value: question.id,
                        qlistParentId: parentId,
                    })
                }

                let questionPath = ['optionSet']
                if (question.questionTypeCode === 'OD') questionPath = ['optionSet', 'options']
                if (question.questionTypeCode === 'TB') questionPath = ['optionSetY', 'options']

                if (parentId) {
                    const parent = questions.find((q) => q.questionId === parentId)

                    if (R.hasPath(['question', 'optionSet'], parent)) {
                        const filteredParentOptions = R.pathOr([], ['optionSet', 'options'], parent.question)
                            .filter(({ isNonReporting }) => !isNonReporting)

                        if (question.optionSet) {
                            question.optionSet.options = [
                                ...filteredParentOptions,
                                ...question.optionSet.options,
                            ]
                        } else if (question.optionSetY) {
                            question.optionSetY.options = [
                                ...filteredParentOptions,
                                ...question.optionSetY.options,
                            ]
                        }
                    }
                }

                const formQuestion = R.pathOr(
                    [],
                    questionPath,
                    question,
                ).map((o) => {
                    let questionOption = {
                        ...o.question,
                        questionTypeCode: question.questionTypeCode === 'FM' ? o.question.questionTypeCode : o.questionTypeCode || `${question.questionTypeCode} - row`,
                        label: unescapeHtmlTags(stripOutHtml(this.getQuestionLabel(
                            question,
                            langcode,
                            ['TB', 'OD'].includes(question.questionTypeCode)
                                ? R.pathOr({}, ['langs'], o)
                                : R.pathOr({}, ['question', 'content', 'shortTitle'], o),
                        ))),
                        value: R.pathOr('', ['TB', 'OD'].includes(question.questionTypeCode) ? ['id'] : ['question', 'id'], o),
                        parentId: question.questionTypeCode !== 'FM' ? question.id : undefined,
                        formId: question.questionTypeCode === 'FM' ? question.id : undefined,
                        group,
                        respondentType,
                        qlistParentId: parentId,
                    }
                    if (['TB', 'OD'].includes(question.questionTypeCode)) {
                        questionOption = { ...question, ...questionOption }
                    }
                    return questionOption
                })
                if (question.questionTypeCode !== 'FM') {
                    formQuestion.push({
                        ...question,
                        group,
                        respondentType,
                        label: `${questionLabel} - Total`,
                        value: question.id,
                        qlistParentId: parentId,
                    })
                }
                return formQuestion
            }).flat(2).sort((a, b) => ((a.label > b.label) ? 1 : ((b.label > a.label) ? -1 : 0)))
            .sort((a, b) => (((a.parentId || a.value) > (b.parentId || b.value)) ? 1
                : (((b.parentId || b.value) > (a.parentId || a.value)) ? -1 : 0)))

        // sort by label then ID to get them in alphabetical order but grouped by question
    )

    setRegistrationDataQuestions = (surveyDetailed) => {
        const pages = R.pathOr([], ['survey', 'pages'], surveyDetailed)
        const questions = pages.flatMap((p) => p.questions)
        this.registrationDataQuestions = questions.reduce((rq, q) => {
            if (q && q.hasPrepopulatedData) return [...rq, q.questionId]
            return rq
        }, [])
    }

    getEventLevelQuesions = async (metric) => {
        const { appStore, newWidget } = this
        const respondentType = R.pathOr(newWidget.respondentType, ['respondentType'], metric)
        const surveyType = R.pathOr(newWidget.surveyType, ['surveyType'], metric)
        const industry = R.pathOr(newWidget.industry, ['industry'], metric)
        const { langcode } = appStore
        const { recordId, surveys } = this.dashStore

        const surveyBasic = surveys
            .filter((s) => (
                s.productId === recordId
                && s.respondentType === respondentType
                && s.surveyType === surveyType
                && s.industry === industry
                && ((s.status !== 4
                && [1, 2, 3, 6].includes(s.productStatus)
                && s.isPrimary) || surveyType === 'EM')
            ))
            .reduce((s, cs) => (cs.year > s.year ? cs : s), { year: 0 })
        if (!surveyBasic || !surveyBasic.surveyId) {
            this.detailedQuestions = []
            return
        }
        const surveyDetailed = await this.getSurveyQuestions(surveyBasic.surveyId)

        this.fullSurvey = surveyDetailed

        this.setRegistrationDataQuestions(surveyDetailed)

        const surveyQuestions = surveyDetailed.survey.pages
            .reduce((q, p) => {
                q.push(p.questions)
                return q
            }, []).flat(2).filter((q) => !!q)

        const pageQuestionIdToQuestionId = {}
        const parentIds = {}
        surveyQuestions.forEach(({ id, questionId, parentId }) => {
            pageQuestionIdToQuestionId[id] = questionId
            if (parentId) {
                parentIds[questionId] = pageQuestionIdToQuestionId[parentId]
            }
        })

        this.questions = await Promise.all(
            surveyQuestions.map((q) => this.getQuestion(q.questionId, true)),
        )

        this.questions = this.questions
            .filter(({ question }) => !this.blockedEventLevelQuestions.includes(question.questionTypeCode))
            .map((q) => ({
                ...q,
                parentId: parentIds[q.questionId],
            }))

        const { metadata } = surveyDetailed.survey
        const { productInfo } = metadata
        const { industrySpecificInfo } = productInfo
        const { productType } = industrySpecificInfo
        const group = this.getRespondentTypeName(industry, productType, surveyType, respondentType)

        this.detailedQuestions = this.formatQuestions(
            this.questions,
            langcode,
            group,
            respondentType,
        )
        this.detailedQuestionsMap.set(`${industry}-${productType}-${surveyType}-${respondentType}-true`, this.formatQuestions(
            this.questions,
            langcode,
            group,
            respondentType,
        ))
    }

    getSurveyQuestions = (surveyId) => this.appStore.http.get(`/survey/${surveyId}`)

    getQlistQuestions = async (metric, recordOverwrite, oIdOverwrite, respondentTypeOverwrite, surveyTypeOverwrite, productTypeOverwrite) => {
        const { appStore, newWidget } = this
        const oId = oIdOverwrite || this.dashStore.oId
        const recordId = recordOverwrite || this.dashStore.recordId
        const productType = R.pathOr(metric.productType, ['productType', 'value'], metric)
        const surveyType = R.pathOr(metric.surveyType, ['surveyType', 'value'], metric)
        const respondentType = R.pathOr(metric.respondentType, ['respondentType', 'value'], metric)
        const includeExplori = surveyType === 'EM' ? '&includeExplori=true' : ''
        const url = `/qlist/getAllQlists2?productType=${productTypeOverwrite || productType}&surveyType=${surveyTypeOverwrite || surveyType}&respondentType=${respondentTypeOverwrite || respondentType}${includeExplori}`
        const allQuestions = await appStore.http.get(url)
        const filteredQuestions = R.uniqBy(R.prop('questionId'))(
            oId > 3
                ? allQuestions.filter((q) => (
                    (q.recordId === recordId && q.qlistoid === oId) || q.qlistoid < oId))
                : allQuestions,
        )

        const uniqueQuestionIds = []
        return filteredQuestions.reduce((qs, q) => {
            const question = JSON.parse(q.question)
            if (!uniqueQuestionIds.includes(q.questionId.toLowerCase())) {
                uniqueQuestionIds.push(q.questionId.toLowerCase())
                qs.push({ ...q, question, questionOid: R.pathOr(0, ['metadata', 'oid'], question) })
            }
            return qs
        }, [])
    }

    getQuestion = async (questionId, appendQuestionId = false) => {
        const { appStore } = this
        const url = `/api/question/${questionId.toUpperCase()}/7`
        const question = await appStore.http.get(url)
        if (appendQuestionId) {
            question.questionId = questionId
            return question
        }
        return question
    }

    resetStore = () => {
        this.newWidget = new NewWidget(undefined, undefined, this.appStore)
    }

    changeNonWidgetReset = (kpi) => {
        const { newWidget } = this

        const {
            widgetBasis,
        } = newWidget
        const respondentType = R.pathOr(newWidget.respondentType, ['respondentType', 'value'], newWidget)
        const surveyType = R.pathOr(newWidget.surveyType, ['surveyType', 'value'], newWidget)
        const productType = R.pathOr(newWidget.productType, ['productType', 'value'], newWidget)
        const industry = R.pathOr(newWidget.industry, ['industry', 'value'], newWidget)

        const kpiSettings = R.pathOr({}, ['settings'], kpi)

        this.newWidget = {
            widgetBasis,
            industry,
            respondentType,
            surveyType,
            productType,
            questionId: '',
            parentQuestion: '',
            displayAs: '',
            questionTypeCode: '',
            isNumeric: '',
            question: undefined,
            dataLevel: '',
            timePeriod: '',
            filter: [],
            chart: '',
            defaultSize: 2,
            multiQuestion: [{}, {}],
            nonQuestionType: '',
            quadrant: undefined,
            widgetTitle: '',
            nonQuestionKpiType: '',
            widgetRenderSettings: {},
            kpiRenderSettings: {
                format: R.pathOr(undefined, ['format'], kpiSettings),
                dataDecimals: R.pathOr(undefined, ['decimals'], kpiSettings),
            },
            customTag: undefined,
            tagOptions: [],
            optionFilter: [],
            includeRegistrationData: false,
        }
    }

    toggleShow = (newValue) => {
        if (this.show) {
            this.resetStore()
        }
        this.show = typeof newValue === 'boolean' ? newValue : !this.show
    }

    createWidget = () => {
        // TODO: Create the dahboard with settings from wizard
        this.toggleShow()
    }

    setError = () => {
        this.error = true
    }

    setDisplayAs = ({ value }) => {
        this.newWidget.changeNewWidgetBulk([{ field: 'kpiType', value }])
    }

    getProdMap = async () => {
        const { appStore } = this
        const { clientId } = appStore.userData
        const productMaps = await appStore.http.get(`/productmap/${clientId}`)
        const prodMap = productMaps.reduce((obj, m) => {
            const newObj = { ...obj }
            // eslint-disable-next-line max-len
            newObj[m.industrycode] = newObj[m.industrycode] || { label: m.industryname, value: m.industrycode, children: {} }
            // eslint-disable-next-line max-len
            newObj[m.industrycode].children[m.producttypecode] = newObj[m.industrycode].children[m.producttypecode]
            || { label: m.producttypename, value: m.producttypecode, children: {} }
            // eslint-disable-next-line max-len
            newObj[m.industrycode].children[m.producttypecode].children[m.surveytypecode] = newObj[m.industrycode].children[m.producttypecode].children[m.surveytypecode]
            || { label: m.surveytypename, value: m.surveytypecode, children: {} }
            // eslint-disable-next-line max-len
            newObj[m.industrycode].children[m.producttypecode].children[m.surveytypecode].children[m.respondenttypecode] = newObj[m.industrycode].children[m.producttypecode].children[m.surveytypecode].children[m.respondenttypecode]
            || { label: m.respondenttypename, value: m.respondenttypecode }

            return newObj
        }, {})
        return prodMap
    }

    addWidget = () => {
        const { dashStore } = this
        const formattedWidget = this.newWidget.newWidgetToWidget()
        dashStore.addWidget(formattedWidget)
        return formattedWidget
    }

    getNonQuestionData = async () => {
        this.loading = true
        await this.getKpis()
        this.loading = false
    }

    getKpis = async () => {
        try {
            const kpis = await this.appStore.http.get('/kpi')
            this.kpis = kpis
        } catch (e) {
            this.kpis = []
        }
    }

    getDefaultTitle = () => {
        const { newWidget, kpis, appStore } = this
        const { langcode } = appStore
        if (newWidget.widgetTitle) return newWidget.widgetTitle
        if (newWidget.nonQuestionType === 'quadrantAnalysis') return 'Quadrant Analysis'
        const respondentType = R.pathOr(newWidget.respondentType, ['respondentType', 'value'], newWidget)

        let defaultTitle
        if (newWidget.widgetBasis !== 'question') {
            defaultTitle = newWidget.nonQuestionType === 'KPI'
                ? kpis.find((k) => (
                    k.kpiId === newWidget.nonQuestionKpiType
                        && k.respondentType === respondentType
                )).name
                : newWidget.nonQuestionType
        } else {
            defaultTitle = R.pathOr('', ['question', 'content', 'shortTitle', langcode, 'text'], newWidget)
        }

        return defaultTitle
    }

    getAllowedKpis = () => {
        const allowedKpiQuestions = this.questions.reduce((set, q) => (
            set.add(q.questionId.toLowerCase())
        ), new Set())
        return this.kpis
            .filter((k) => (
                R.pathOr(
                    false,
                    [
                        'productMap',
                        k.industry,
                        'children',
                        k.productType,
                        'children',
                        k.surveyType,
                        'children',
                        k.respondentType,
                    ],
                    this,
                )
            ))
            .reduce(({ kpis, allowedKpis }, kpi) => {
                if (hasRequiredDependencies(kpi, allowedKpiQuestions, allowedKpis)) {
                    kpis.push(kpi)
                    allowedKpis.add(kpi.kpiId.toLowerCase())
                }
                return {
                    kpis,
                    allowedKpis,
                }
            }, {
                kpis: [],
                allowedKpis: new Set(),
            }).kpis
    }

    getRespondentTypeName = (industry, productType, surveyType, respondentType) => R.pathOr(
        'KPIs',
        [
            'productMap',
            industry,
            'children',
            productType,
            'children',
            surveyType,
            'children',
            respondentType,
            'label',
        ],
        this,
    )

    getQuadrantQuestions = (withLMAT = true, forMultiQuestion = false) => {
        const { newWidget } = this
        const productType = R.pathOr(newWidget.productType, ['productType', 'value'], newWidget)
        const quadrantAllowedQuestions = this.questions
            .filter((q) => !!q.allowQuadrantAnalysis || forMultiQuestion)
            .reduce((obj, q) => (
                {
                    ...obj,
                    [q.questionId.toLowerCase()]:
                    {
                        respondenttypename: q.respondenttypename,
                        respondentTypeCode: q.respondentTypecode,
                    },
                }
            ), {})

        const quadrantKpis = this.getAllowedKpis()
            .filter((kpi) => kpi.settings.allowQuadrantAnalysis && kpi.productType === productType)

        const groupedQuestions = R.groupBy((q) => q.group,
            [
                ...quadrantKpis.map((k) => ({
                    label: k.name,
                    value: k.kpiId,
                    group: this.getRespondentTypeName(k.industry, k.productType, k.surveyType, k.respondentType),
                    respondentType: k.respondentType,
                })),
                ...this.detailedQuestions
                    .filter((q) => Object.keys(quadrantAllowedQuestions).includes(q.id.toLowerCase()) || forMultiQuestion)
                    .map((q) => {
                        const questionId = q.formId || q.parentId || q.id
                        return {
                            ...q,
                            group: R.or(q.group, quadrantAllowedQuestions[questionId.toLowerCase()].respondenttypename),
                            respondentType: R.or(q.respondentType, quadrantAllowedQuestions[questionId.toLowerCase()].respondentTypeCode),
                        }
                    }).filter((q) => (forMultiQuestion ? !['TB', 'NF', 'FM'].includes(q.questionTypeCode) : true)),
            ])

        return Object.keys(groupedQuestions).map((g) => ({ label: g, options: groupedQuestions[g] }))
    }

    getMultiQuadrantQuestions = (withLMAT = true, forMultiQuestion = false, multiIndex, quadrantIndex) => {
        const { newWidget } = this
        const productType = R.pathOr(newWidget.productType, ['quadrant', quadrantIndex[multiIndex], 'productType'], newWidget)

        const quadrantAllowedQuestions = (this.multiQuestions.get(multiIndex) || [])
            .filter((q) => !!q.allowQuadrantAnalysis || forMultiQuestion)
            .reduce((obj, q) => (
                {
                    ...obj,
                    [q.questionId.toLowerCase()]:
                    {
                        respondenttypename: q.respondenttypename,
                        respondentTypeCode: q.respondentTypecode,
                    },
                }
            ), {})

        const quadrantKpis = this.getAllowedKpis()
            .filter((kpi) => kpi.settings.allowQuadrantAnalysis && kpi.productType === productType)

        const groupedQuestions = R.groupBy((q) => q.group,
            [
                ...quadrantKpis.map((k) => ({
                    label: k.name,
                    value: k.kpiId,
                    group: this.getRespondentTypeName(k.industry, k.productType, k.surveyType, k.respondentType),
                    respondentType: k.respondentType,
                })),
                ...(this.multiDetailedQuestions.get(multiIndex) || [])
                    .filter((q) => Object.keys(quadrantAllowedQuestions).includes(q.id.toLowerCase()) || forMultiQuestion)
                    .map((q) => {
                        const questionId = q.formId || q.parentId || q.id
                        return {
                            ...q,
                            group: R.or(q.group, quadrantAllowedQuestions[questionId.toLowerCase()].respondenttypename),
                            respondentType: R.or(q.respondentType, quadrantAllowedQuestions[questionId.toLowerCase()].respondentTypeCode),
                        }
                    }).filter((q) => (forMultiQuestion ? !['TB', 'NF', 'FM'].includes(q.questionTypeCode) : true)),
            ])

        return Object.keys(groupedQuestions).map((g) => ({ label: g, options: groupedQuestions[g] }))
    }

    getBenchmarkTags = async () => {
        const benchmarkTags = await this.appStore.http.get('/benchmarkTags')
        this.benchmarkTags = benchmarkTags.benchmarkTags
    }

    toggleShowWidgetCreator = () => {
        if (this.showWidgetCreator) this.resetStore()
        this.showWidgetCreator = !this.showWidgetCreator
    }

    getQuestionOptions = () => {
        const { newWidget, getQuestionLabel, dashStore } = this
        const { appStore } = dashStore
        // const { question } = newWidget
        const question = R.pathOr({}, ['metrics', 0, 'question'], newWidget)
        const { langcode } = appStore
        if (!question) return []
        const { questionTypeCode } = question

        const questionPath = questionTypeCode.includes('TB')
            ? ['optionSetX', 'options']
            : ['optionSet', 'options']

        const options = R.pathOr([], questionPath, question)

        return options.map((o) => ({
            value: R.pathOr('', ['id'], o),
            label: unescapeHtmlTags(stripOutHtml(getQuestionLabel(
                question,
                langcode,
                R.pathOr({}, ['langs'], o),
                false,
            ))),
        }))
    }

    setOptionFilters = () => {
        const { newWidget, getQuestionOptions } = this
        if (newWidget.kpiType !== 'pct') {
            newWidget.changeNewWidgetBulk([{ value: [], field: 'optionFilter' }])
            return
        }
        newWidget.changeNewWidgetBulk([{ value: getQuestionOptions(), field: 'optionFilter' }])
    }

    toggleCollapse = (newPanel) => {
        this.setState({ activePanel: newPanel })
    }

    newMultiQuestionValue = (newValue, index) => {
        const { newWidget } = this
        const { multiQuestion } = newWidget
        const newMultiQ = [...multiQuestion]
        newMultiQ[index] = newValue
        return newMultiQ
    }

    getIndustryOptions = () => {
        const {
            productMap,
        } = this.dashStore
        return getIndustries(productMap, true)
    }

    blurIndustry = (value) => {
        const {
            changeNewWidgetBulk,
            widgetType,
            newWidget,
        } = this
        const { multiIndex } = this.state
        const children = Object.values(value.children)
        let newValues = []
        if (widgetType === 'multi') {
            newValues = [
                {
                    value: this.newMultiQuestionValue(
                        {
                            industry: value.value,
                            productType: '',
                            surveyType: '',
                            respondentType: '',
                            question: '',
                        },
                        multiIndex,
                    ),
                    field: 'multiQuestion',
                },
            ]
        } else if (['single', 'aggregated'].includes(widgetType)) {
            newValues = [
                { value: '', field: 'question' },
                { value: '', field: 'displayAs' },
                { value: value.value, field: 'industry' },
                { value: '', field: 'productType' },
                { value: '', field: 'surveyType' },
                { value: '', field: 'respondentType' },
            ]
        } else if (['quadrantAnalysis'].includes(widgetType)) {
            const axis = this.quadrantIndex[multiIndex]
            const { quadrant } = newWidget
            const metricObject = this.getMetricObject()
            newValues = [
                {
                    value: {
                        ...quadrant,
                        [axis]: {
                            ...metricObject,
                            question: '',
                            displayAs: '',
                            industry: value.value,
                            productType: '',
                            surveyType: '',
                            respondentType: '',
                        },
                    },
                    field: 'quadrant',
                },
            ]
        }
        changeNewWidgetBulk(newValues)
        if (children.length === 1) {
            this.blurProductType(children[0])
        }
    }

    getProductTypeOptions = (m) => {
        const {
            productMap,
        } = this.dashStore
        const metricObject = m
        const { industry } = metricObject
        return Object.values(R.pathOr({}, [industry, 'children'], productMap))
    }

    blurProductType = (value) => {
        const {
            changeNewWidgetBulk,
            widgetType,
            newWidget,
        } = this
        const { multiIndex } = this.state
        let newValues = []
        const children = Object.values(value.children)

        if (widgetType === 'multi') {
            newValues = [
                {
                    value: this.newMultiQuestionValue(
                        {
                            ...newWidget.multiQuestion[multiIndex],
                            productType: value.value,
                            surveyType: '',
                            respondentType: '',
                            question: '',
                        },
                        multiIndex,
                    ),
                    field: 'multiQuestion',
                },
            ]
        } else if (['single', 'aggregated'].includes(widgetType)) {
            newValues = [
                { value: '', field: 'question' },
                { value: value.value, field: 'productType' },
                { value: '', field: 'displayAs' },
                { value: '', field: 'surveyType' },
                { value: '', field: 'respondentType' },
            ]
        } else if (['quadrantAnalysis'].includes(widgetType)) {
            const axis = this.quadrantIndex[multiIndex]
            const { quadrant } = newWidget
            const metricObject = this.getMetricObject()
            newValues = [
                {
                    value: {
                        ...quadrant,
                        [axis]: {
                            ...metricObject,
                            question: '',
                            displayAs: '',
                            productType: value.value,
                            surveyType: '',
                            respondentType: '',
                        },
                    },
                    field: 'quadrant',
                },
            ]
        }

        changeNewWidgetBulk(newValues)
        if (children.length === 1) {
            this.blurSurveyType(children[0])
        }
    }

    getSurveyTypeOptions = (m) => {
        const {
            productMap,
        } = this.dashStore
        const metricObject = m
        const { industry, productType } = metricObject

        return Object.values(R.pathOr({}, [industry, 'children', productType, 'children'], productMap))
    }

    blurSurveyType = (value) => {
        const {
            changeNewWidgetBulk,
            widgetType,
            newWidget,
        } = this
        const { multiIndex } = this.state
        let newValues = []
        const children = Object.values(value.children)

        if (widgetType === 'multi') {
            newValues = [
                {
                    value: this.newMultiQuestionValue(
                        {
                            ...newWidget.multiQuestion[multiIndex],
                            surveyType: value.value,
                            respondentType: '',
                            question: '',
                        },
                        multiIndex,
                    ),
                    field: 'multiQuestion',
                },
            ]
        } else if (['single', 'aggregated'].includes(widgetType)) {
            newValues = [
                { value: '', field: 'question' },
                { value: value.value, field: 'surveyType' },
                { value: '', field: 'displayAs' },
                { value: '', field: 'respondentType' },
            ]
        } else if (['quadrantAnalysis'].includes(widgetType)) {
            const axis = this.quadrantIndex[multiIndex]
            const { quadrant } = newWidget
            const metricObject = this.getMetricObject()
            newValues = [
                {
                    value: {
                        ...quadrant,
                        [axis]: {
                            ...metricObject,
                            question: '',
                            displayAs: '',
                            surveyType: value.value,
                            respondentType: '',
                        },
                    },
                    field: 'quadrant',
                },
            ]
        }

        changeNewWidgetBulk(newValues)
        if (children.length === 1) {
            this.blurRespondentTypes(children[0])
        }
    }

    getRespondentTypeOptions = (m) => {
        const {
            productMap,
        } = this.dashStore
        const metricObject = m
        const { industry, productType, surveyType } = metricObject

        return Object.values(R.pathOr(
            {},
            [industry, 'children', productType, 'children', surveyType, 'children'],
            productMap,
        ))
    }

    blurRespondentTypes = async (value) => {
        const {
            changeNewWidgetBulk,
            widgetType,
            newWidget,
            getQuestions,
            getMultiQuestions,
        } = this
        const { multiIndex } = this.state
        let newValues = []

        if (widgetType === 'multi') {
            newValues = [
                {
                    value: this.newMultiQuestionValue(
                        {
                            ...newWidget.multiQuestion[multiIndex],
                            respondentType: value.value,
                            question: '',
                        },
                        multiIndex,
                    ),
                    field: 'multiQuestion',
                },
            ]
        } else if (['single', 'aggregated'].includes(widgetType)) {
            newValues = [
                { value: '', field: 'question' },
                { value: '', field: 'displayAs' },
                { value: value.value, field: 'respondentType' },
            ]
        } else if (['quadrantAnalysis'].includes(widgetType)) {
            const axis = this.quadrantIndex[multiIndex]
            const { quadrant } = newWidget
            const metricObject = this.getMetricObject()
            newValues = [
                {
                    value: {
                        ...quadrant,
                        [axis]: {
                            ...metricObject,
                            question: '',
                            displayAs: '',
                            respondentType: value.value,
                        },
                    },
                    field: 'quadrant',
                },
            ]
        }

        changeNewWidgetBulk(newValues)
        if (['multi', 'quadrantAnalysis'].includes(widgetType)) {
            await getMultiQuestions(multiIndex, this.quadrantIndex)
        } else {
            await getQuestions()
        }
    }

    blurQuestion = (value) => {
        const {
            changeNewWidgetBulk,
            widgetType,
            newWidget,
        } = this
        const { multiIndex } = this.state
        let newValues = []
        this.setDisplayAs({ name: undefined })

        if (widgetType === 'multi') {
            newValues = [
                {
                    value: this.newMultiQuestionValue(
                        {
                            ...newWidget.multiQuestion[multiIndex],
                            question: {
                                ...value,
                                isNps: R.pathOr(false, ['settings', 'frontend', 'renderNPS'], value)
                                            || (R.pathOr(false, ['metadata', 'kpiType'], value) === 'NPS'),
                            },
                            questionId: value.value,
                            parentQuestion: value.parentId,
                            questionTypeCode: value.questionTypeCode,
                            value: value.value,
                            name: multiIndex + 1,
                        },
                        multiIndex,
                    ),
                    field: 'multiQuestion',
                },
                { value: 'other', field: 'widgetBasis' },
            ]
        } else if (widgetType === 'single') {
            newValues = [
                { value, field: 'question' },
                { value: value.value, field: 'questionId' },
                { value: value.parentId, field: 'parentQuestion' },
                { value: value.questionTypeCode, field: 'questionTypeCode' },
                { value: 'question', field: 'widgetBasis' },
                { value: '', field: 'displayAs' },
            ]
        } else if (['quadrantAnalysis'].includes(widgetType)) {
            const axis = this.quadrantIndex[multiIndex]
            const { quadrant } = newWidget
            const metricObject = this.getMetricObject()
            newValues = [
                { value: 'other', field: 'widgetBasis' },
                {
                    value: {
                        ...quadrant,
                        [axis]: {
                            ...metricObject,
                            question: value,
                            value: value.value,
                            group: value.group,
                            questionId: value.value,
                            parentQuestion: value.parentId,
                            questionTypeCode: value.questionTypeCode,

                            displayAs: '',
                        },
                    },
                    field: 'quadrant',
                },
            ]
        }

        changeNewWidgetBulk(newValues)
        this.setIsNumeric(value)
        if (widgetType === 'single') {
            this.blurDisplayAs({ name: this.getDisplayAsOptions()[0].value, value: true })
        } else {
            this.setOptionFilters()
        }
    }

    getDisplayAsOptions = () => {
        const { newWidget } = this
        const { isNumeric } = newWidget
        const questionTypeCode = R.pathOr('', ['metrics', 0, 'question', 'questionTypeCode'], newWidget)
        return R.pathOr(
            [],
            ['questionDisplayAs', questionTypeCode, isNumeric],
            this,
        )
    }

    blurDisplayAs = (v) => {
        this.setDisplayAs(v)
        this.setOptionFilters()
    }

    getQuestionControlOptions = (metric) => {
        const {
            widgetType, detailedQuestions, multiDetailedQuestions, getMultiQuadrantQuestions, newWidget,
        } = this

        const multiIndex = newWidget.metrics.findIndex((m) => m.id === metric.id)

        // if (widgetType === 'single') return detailedQuestions
        // if (widgetType === 'multi') return multiDetailedQuestions.get(multiIndex) || []
        // if (widgetType === 'quadrantAnalysis') return getMultiQuadrantQuestions(false, false, multiIndex, this.quadrantIndex)
        return detailedQuestions
    }

    renderMetricControls = () => {
        const {
            newWidget,
            changeNewWidget,
            changeNewWidgetBulk,
            detailedQuestions,
            multiDetailedQuestions,
            loadingQuestions,
            widgetType,
            getAllowedKpis,
            registrationDataQuestions,
        } = this

        const metricObject = this.getMetricObject()
        const isMulti = ['multi', 'quadrantAnalysis'].includes(widgetType)
        const { multiIndex } = this.state
        const kpiOptions = widgetType === 'aggregated' && newWidget.respondentType ? getAllowedKpis()
            .filter((k) => (
                k.respondentType === R.pathOr(newWidget.respondentType, ['respondentType', 'value'], newWidget)
                && k.industry === R.pathOr(newWidget.industry, ['industry', 'value'], newWidget)
                && k.surveyType === R.pathOr(newWidget.surveyType, ['surveyType', 'value'], newWidget)
                && k.productType === R.pathOr(newWidget.productType, ['productType', 'value'], newWidget)
            )).map((k) => (
                {
                    label: k.name,
                    value: k.kpiId,
                    isMatrixKpi: !!k.settings.quadrantNames,
                    matrixAxisNames: k.settings.quadrantNames ? Object.fromEntries(k.variables.map(({ name }) => name.split('-'))) : undefined,
                }
            )) : []

        const industries = this.getIndustryOptions()
        const productTypes = this.getProductTypeOptions()
        const surveyTypes = this.getSurveyTypeOptions()
        const respondentTypes = this.getRespondentTypeOptions()
        const displayAsOptions = this.getDisplayAsOptions()
        const questionOptions = this.getQuestionControlOptions()
        return (
            <div className="controls">
                <Select
                    key={`industry ${multiIndex}`}
                    label="industry"
                    name="industry"
                    options={industries}
                    onBlur={this.blurIndustry}
                    value={industries.find((i) => i.value === metricObject.industry)}
                    showDisabled={!isMulti && industries.length < 2}
                />
                <Select
                    key={`productType ${multiIndex}`}
                    label="Event Type"
                    name="productType"
                    options={productTypes}
                    onBlur={this.blurProductType}
                    value={productTypes.find((p) => p.value === metricObject.productType)}
                    showDisabled={!isMulti && productTypes.length < 2}
                />
                <Select
                    key={`surveyType ${multiIndex}`}
                    label="Survey Type"
                    name="surveyType"
                    options={surveyTypes}
                    onBlur={this.blurSurveyType}
                    value={surveyTypes.find((s) => s.value === metricObject.surveyType)}
                    showDisabled={!isMulti && surveyTypes.length < 2}
                />
                <Select
                    key={`respondentType ${multiIndex}`}
                    label="Respondent Type"
                    name="respondentType"
                    options={respondentTypes}
                    onBlur={this.blurRespondentTypes}
                    value={respondentTypes.find((r) => r.value === metricObject.respondentType)}
                    showDisabled={!isMulti && respondentTypes.length < 2}
                />
                {['single', 'multi', 'quadrantAnalysis'].includes(widgetType) ? (
                    <Select
                        key={`question ${multiIndex}`}
                        label="Question"
                        name="question"
                        options={questionOptions}
                        onBlur={this.blurQuestion}
                        value={questionOptions.find((q) => q.value === metricObject.question?.value)}
                        showDisabled={(!isMulti && !metricObject.respondentType) || loadingQuestions}
                        isLoading={loadingQuestions}
                    />
                ) : null }
                {['aggregated'].includes(widgetType) ? (
                    <Select
                        key={`kpi ${multiIndex}`}
                        label="Kpi"
                        name="kpi"
                        options={kpiOptions}
                        onBlur={(value) => {
                            // changeNonWidgetReset(kpi)
                            changeNewWidget('other', 'widgetBasis')
                            changeNewWidget('KPI', 'nonQuestionType')
                            changeNewWidget(value.value, 'nonQuestionKpiType')
                            changeNewWidget(value.isMatrixKpi, 'isMatrixKpi')
                            changeNewWidget({ ...newWidget.widgetRenderSettings, matrixAxisNames: value.matrixAxisNames }, 'widgetRenderSettings')
                        }}
                        value={newWidget.respondentType && kpiOptions.length
                            ? kpiOptions.find(({ value }) => value === newWidget.nonQuestionKpiType)
                            : ''}
                        showDisabled={(!isMulti && !newWidget.respondentType) || loadingQuestions}
                        isLoading={loadingQuestions}
                    />
                ) : null }
                {widgetType === 'single' ? (
                    <CheckboxGroup
                        key={`displayAs ${multiIndex}`}
                        cssClass="formControl"
                        label={(
                            <div className="selectLabel">
                                {i18n.t('dynamicDash.wizard.displayAsLabel')}
                                <i className="fa-solid fa-circle-question">
                                    <Tooltip text={i18n.t('dynamicDash.wizard.displayAsTooltip')} direction="bottom" />
                                </i>
                            </div>
                        )}
                        options={displayAsOptions}
                        defaultChecked={R.pathOr([], ['newWidget', 'displayAs'], this)}
                        refreshOnRender
                        radio
                        name="displayAs"
                        isMandatory
                        radioValue={R.pathOr('', ['newWidget', 'displayAs'], this)}
                        onBlur={this.blurDisplayAs}
                    />
                ) : null }
                {['single', 'multi'].includes(widgetType)
                && registrationDataQuestions.includes(R.pathOr(undefined, ['question', 'value'], metricObject)) ? (
                    <Checkbox
                            key={`includeRegistrationData ${multiIndex}`}
                            cssClass="formControl"
                            label="Include Registration data"
                            defaultChecked={R.pathOr(false, ['newWidget', 'includeRegistrationData'], this)}
                            name="includeRegistrationData"
                            onBlur={({ value }) => changeNewWidget(value, 'includeRegistrationData')}
                        />
                    ) : null }
            </div>
        )
    }

    changeDataLevel = (value, field) => {
        const { changeNewWidgetBulk } = this.newWidget
        this.createTreeData(
            value,
            R.pathOr([], ['newWidget', 'filter'], this),
        )
        changeNewWidgetBulk([{ value: value.value, field }])
    }

    getTimePeriodOptions = () => {
        const { dashStore, newWidget } = this
        const { oId } = dashStore

        return oId === 6 ? [
            { value: 'YOY', label: 'Year On Year' },
        ]
            : R.pathOr([], ['timePeriodOptions', newWidget.scope], this)
    }

    renderWidgetScope = () => {
        const {
            changeNewWidget,
            newWidget,
        } = this

        const { activePanel } = this.state

        return (
            <Panel style={{ overflow: 'visible' }} cssClass={`widgetScope ${activePanel === 'widgetScope' ? '' : 'collapsed'}`}>
                <div
                    className="collapseIcon"
                    onClick={() => this.toggleCollapse(activePanel === 'widgetScope' ? undefined : 'widgetScope')}
                >
                    <div
                        className="collapseIconContainer"
                    >
                        {
                            activePanel === 'widgetScope'
                                ? <i className="fa-regular fa-chevron-left" />
                                : <i className="fa-regular fa-chevron-right" />
                        }
                    </div>
                </div>
                <div className="collapsedMenuLabel" style={{ display: activePanel !== 'widgetScope' ? 'block' : 'none' }}>
                    Widget Scope
                </div>
                <div className="controls">
                    <div className="title">
                        <h2>Widget Scope</h2>
                    </div>
                    <Select
                        label={i18n.t('dynamicDash.wizard.dataLevelLabel')}
                        name="dataLevel"
                        options={R.pathOr([], ['dataLevels'], this)}
                        onBlur={({ value }) => {
                            this.changeDataLevel(value, 'dataLevel')
                        }}
                        isMandatory
                        value={R.pathOr([], ['dataLevels'], this).find(({ value }) => value === newWidget.dataLevel)}
                    />
                    <Select
                        label={i18n.t('dynamicDash.wizard.timePeriodLabel')}
                        name="timePeriod"
                        options={this.getTimePeriodOptions()}
                        onBlur={({ value }) => {
                            changeNewWidget(value, 'timePeriod')
                        }}
                        isMandatory
                        value={this.getTimePeriodOptions().find(({ value }) => value === newWidget.timePeriod)}
                    />
                    <StagedForm
                        store={this}
                        controls={this.widgetScopeControls().filter((c) => !!c)}
                        valueHolder={this.newWidget}
                    />
                </div>
            </Panel>
        )
    }

    metricPanel = (controls) => {
        const { activePanel } = this.state
        return (
            <Panel style={{ overflow: 'visible' }} cssClass={`metrics ${activePanel === 'metrics' ? '' : 'collapsed'}`}>
                <div
                    className="collapseIcon"
                    onClick={() => this.toggleCollapse(activePanel === 'metrics' ? undefined : 'metrics')}
                >
                    <div
                        className="collapseIconContainer"
                    >
                        {
                            activePanel === 'metrics'
                                ? <i className="fa-regular fa-chevron-left" />
                                : <i className="fa-regular fa-chevron-right" />
                        }
                    </div>
                </div>
                <div
                    className="collapsedMenuLabel"
                    style={{ display: activePanel !== 'metrics' ? 'block' : 'none' }}
                >
                    Metrics
                </div>
                <div className="panelBody">
                    {controls}
                </div>
            </Panel>
        )
    }

    renderMetricSelector = () => {
        const { newWidget, changeNewWidget, widgetType } = this
        const { multiQuestion } = newWidget
        const { multiIndex } = this.state
        const metrics = widgetType === 'multi' ? multiQuestion : ['X Axis', 'Y Axis', 'Z Axis']
        const metricTabs = metrics.map((m, i) => (
            <div
                onClick={() => this.setState({ multiIndex: i })}
                className={`metric ${multiIndex === i ? 'active' : ''}`}
            >
                {widgetType === 'multi' ? `Metric ${i + 1}` : m}
            </div>
        ))

        if (widgetType === 'multi' && multiQuestion.length < 6) {
            metricTabs.push(
                <div
                    onClick={() => changeNewWidget(this.newMultiQuestionValue({}, multiQuestion.length), 'multiQuestion')}
                    className="metric addMetric"
                >
                    Add metric...
                </div>,
            )
        }
        return (
            <div className="metricSelector">
                {metricTabs}
            </div>
        )
    }

    renderSingleMetricSettings = () => {
        const { widgetKey } = this.props
        const { newWidget } = this
        const { loading } = newWidget
        return (
            <div className="singleMetric">
                {this.metricPanel(
                    <>
                        <div className="title">
                            <h2>Metrics</h2>
                        </div>
                        {this.renderMetricControls()}
                    </>,
                )}
                {this.renderWidgetScope()}
                <ChartPreviewContainer loading={loading} this={this} editing={!!widgetKey} />
            </div>
        )
    }

    renderMultiMetricSettings = () => {
        const { widgetKey } = this.props
        const { newWidget } = this
        const { loading } = newWidget
        return (
            <div className="singleMetric">
                {this.metricPanel(
                    <>
                        <div className="title">
                            <h2>Metrics</h2>
                        </div>
                        {this.renderMetricSelector()}
                        {this.renderMetricControls()}
                    </>,
                )}
                {this.renderWidgetScope()}
                <ChartPreviewContainer loading={loading} this={this} editing={!!widgetKey} />
            </div>
        )
    }

    renderMetricSettings = () => {
        const { widgetType } = this
        switch (widgetType) {
            case 'single':
            case 'aggregated':
                return this.renderSingleMetricSettings()
            case 'multi':
                return this.renderMultiMetricSettings()
            case 'quadrantAnalysis':
                return this.renderMultiMetricSettings()
            default:
                return null
        }
    }

    closeShowWidgetCreator = () => {
        const { dashStore, resetStore } = this
        const { toggleShowWidgetCreator } = dashStore
        resetStore()
        toggleShowWidgetCreator()
    }

    updateLayout = (newKey) => {
        const { widgetKey } = this.props
        const { dashStore } = this
        const { layouts } = dashStore
        return Object.keys(layouts).reduce((layout, k) => (
            {
                ...layout,
                [k]: layouts[k]
                    .map((l) => ({ ...l, i: l.i === widgetKey ? newKey : l.i })),
            }
        ), {})
    }

    updateWidget = () => {
        const { widgetKey } = this.props
        const { dashStore } = this
        const { updateWidget, updateLayouts, saveDashboard } = dashStore
        const widgets = R.pathOr([{}], ['widgets'], dashStore)
        const widgetIndex = widgets.findIndex((w) => w.key === widgetKey)
        const newKey = uuidv4()
        const newLayout = this.updateLayout(newKey)
        const widget = this.addWidget(true)
        widget.key = newKey
        updateWidget(widgetIndex, widget)
        updateLayouts(newLayout)
        saveDashboard()
    }

    toggleShowWidgetCreatorV3 = () => {
        this.showWidgetCreatorV3 = !this.showWidgetCreatorV3
    }

    renderWidgetMetricControls = (widgetMetric, metricIndex) => {
        const industryOptions = this.getIndustryOptions()
        if (industryOptions.length === 1) {
            widgetMetric.changeNewWidgetMetricBulk([{ value: industryOptions[0].value, field: 'industry' }])
        }
        const productTypes = this.getProductTypeOptions(widgetMetric)
        if (productTypes.length === 1) {
            widgetMetric.changeNewWidgetMetricBulk([{ value: productTypes[0].value, field: 'productType' }])
        }
        const surveyTypes = this.getSurveyTypeOptions(widgetMetric)
        if (surveyTypes.length === 1) {
            widgetMetric.changeNewWidgetMetricBulk([{ value: surveyTypes[0].value, field: 'surveyType' }])
        }
        const respondentTypes = this.getRespondentTypeOptions(widgetMetric)
        if (respondentTypes.length === 1) {
            widgetMetric.changeNewWidgetMetricBulk([
                { value: respondentTypes[0].value, field: 'respondentType' },
                { value: respondentTypes[0].label, field: 'group' },
            ])
            this.getQuestions(widgetMetric)
        }
        const allowedKpis = widgetMetric.respondentType ? this.getAllowedKpis() : []
        const kpiOptions = allowedKpis
            .filter((k) => (
                k.respondentType === R.pathOr(widgetMetric.respondentType, ['respondentType', 'value'], widgetMetric)
                        && k.industry === R.pathOr(widgetMetric.industry, ['industry', 'value'], widgetMetric)
                        && k.surveyType === R.pathOr(widgetMetric.surveyType, ['surveyType', 'value'], widgetMetric)
                        && k.productType === R.pathOr(widgetMetric.productType, ['productType', 'value'], widgetMetric)
            )).map((k) => (
                {
                    type: 'KPI',
                    label: k.name,
                    value: k.kpiId,
                    isMatrixKpi: !!k.settings.quadrantNames || k.kpiId === 'LMX',
                    matrixAxisNames: k.settings.quadrantNames ? Object.fromEntries(k.variables.map(({ name }) => name.split('-'))) : undefined,
                }
            ))

        const questionOptions = this.getMetricQuestions(widgetMetric)
        const controls = [
            {
                name: 'industry',
                label: 'Industry',
                type: 'select',
                options: () => industryOptions,
                onChange: (v) => {
                    widgetMetric.changeNewWidgetMetricBulk([{ value: v.value, field: 'industry' }])
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => {
                    if (industryOptions < 2) return 2
                    return 2
                },
            },
            {
                name: 'productType',
                label: 'Event Type',
                type: 'select',
                options: () => productTypes,
                onChange: (v) => {
                    widgetMetric.changeNewWidgetMetricBulk([{ value: v.value, field: 'productType' }])
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => {
                    if (productTypes < 2) return 2
                    return 2
                },
            },
            {
                name: 'surveyType',
                label: 'Survey Type',
                type: 'select',
                options: () => surveyTypes,
                onChange: (v) => {
                    widgetMetric.changeNewWidgetMetricBulk([{ value: v.value, field: 'surveyType' }])
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => {
                    if (surveyTypes < 2) return 2
                    return 2
                },
            },
            {
                name: 'respondentType',
                label: 'Respondent Type',
                type: 'select',
                options: () => respondentTypes,
                onChange: async (v) => {
                    widgetMetric.changeNewWidgetMetricBulk([
                        { value: v.value, field: 'respondentType' },
                        { value: v.label, field: 'group' },
                    ])
                    await this.getQuestions(widgetMetric)
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => {
                    if (respondentTypes < 2) return 2
                    return 2
                },
            },
            {
                name: 'question',
                label: 'question',
                type: 'select',
                options: () => [...questionOptions, ...kpiOptions],
                onChange: (v) => {
                    widgetMetric.changeNewWidgetMetricBulk([
                        { value: v, field: 'question' },
                        { value: v.label, field: 'metricLabel' },
                    ])
                    this.newWidget.changeNewWidgetBulk(
                        [
                            { value: R.pathOr([], ['dataLevels'], this)[0].value, field: 'scope' },
                            { value: v.label, field: 'title' },
                        ],
                    )
                    this.newWidget.changeNewWidgetBulk([
                        { value: this.getTimePeriodOptions()[0].value, field: 'benchmarkType' },
                    ])
                    this.newWidget.setIsNumeric(v, this.dashStore.oId)
                    if (v.type !== 'KPI' && this.newWidget.metrics.length === 1) {
                        this.blurDisplayAs(this.getDisplayAsOptions()[0])
                    }
                    this.newWidget.changeNewWidgetBulk([
                        { value: this.getAllowedCharts()[0].value, field: 'chartType' },
                    ])
                    this.setDefaultWidgetRenderSettings()
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => {
                    if (!widgetMetric.respondentType || this.loadingQuestions) return 1
                    return 2
                },
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
                loading: this.loadingQuestions,
            },
        ]
        return (
            <div
                className="controlBlock"
                key={widgetMetric.id}
            >
                <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <h4 style={{ marginBottom: '16px' }}>
                        Metric
                        {' - '}
                        {metricIndex + 1}
                    </h4>
                    {metricIndex > 0
                        ? (
                            <div
                                className="removeMetric"
                                onClick={() => this.newWidget.removeMetric(metricIndex)}
                            >
                                Remove
                                <i className="fa-light fa-trash-can" />
                            </div>
                        )
                        : null}
                </div>
                <StagedForm
                    store={this}
                    controls={controls}
                    valueHolder={widgetMetric}
                />
            </div>
        )
    }

    renderDataLevelControls = () => {
        const controls = [
            {
                name: 'scope',
                label: i18n.t('dynamicDash.wizard.dataLevelLabel'),
                type: 'select',
                options: () => R.pathOr([], ['dataLevels'], this),
                onChange: async (v) => {
                    this.changeDataLevel(v, 'scope')
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => 2,
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
            },
            {
                name: 'benchmarkType',
                label: i18n.t('dynamicDash.wizard.timePeriodLabel'),
                type: 'select',
                options: () => this.getTimePeriodOptions(),
                onChange: async (v) => {
                    this.newWidget.changeNewWidgetBulk([{ value: v.value, field: 'benchmarkType' }])
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => (this.getTimePeriodOptions().length ? 2 : 0),
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
            },
            {
                name: 'kpiType',
                label: i18n.t('dynamicDash.wizard.displayAsLabel'),
                type: 'select',
                options: () => this.getDisplayAsOptions(),
                onChange: (v) => {
                    this.blurDisplayAs(v)
                    this.newWidget.changeNewWidgetBulk([
                        { value: this.getAllowedCharts()[0].value, field: 'chartType' },
                    ])
                    this.setDefaultWidgetRenderSettings()
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => (this.getDisplayAsOptions().length ? 2 : 0),
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
            },
            {
                name: 'optionFilter',
                type: 'select',
                label: i18n.t('dynamicDash.wizard.optionFiltersLabel'),
                tooltipText: i18n.t('dynamicDash.wizard.optionFiltersTooltip'),
                options: () => this.getQuestionOptions(),
                onChange: (v) => {
                    this.newWidget.changeNewWidgetBulk([{ value: v.value, field: 'optionFilter' }])
                },
                isMandatory: false,
                dontAutoSet: true,
                isMulti: true,
                show: (store) => {
                    if (this.newWidget.kpiType !== 'pct') return 0
                    return 2
                },
                showNumberSelected: true,
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
            },
            {
                name: 'filter',
                type: 'treeViewSelect',
                label: i18n.t('dynamicDash.wizard.optionalFiltersLabel'),
                tooltipText: i18n.t('dynamicDash.wizard.optionalFiltersTooltip'),
                options: (store) => {
                    store.createTreeData(
                        R.pathOr([], ['newWidget', 'scope'], store),
                        R.pathOr([], ['newWidget', 'filter'], store),
                    )

                    return store.filterTree
                },
                onChange: (v) => { this.newWidget.changeNewWidgetBulk([{ value: v.value, field: 'filter' }]) },
                show: (store) => {
                    if (store.newWidget.scope === 'client') return 0
                    if (store.dashStore.oId === 6) return 0
                    if (!store.newWidget.scope) return 0
                    return 2
                },
                validate: (({ value }) => !!value.length),
            },
            process.env.USE_CUSTOM_BENCHMARKS === 'true' ? {
                name: 'customTag',
                type: 'select',
                label: i18n.t('dynamicDash.wizard.groupByLabel'),
                tooltipText: i18n.t('dynamicDash.wizard.groupByTooltip'),
                options: (store) => [
                    { label: 'None', value: 0, tagOptions: [] },
                    ...store.benchmarkTags.map((b) => (
                        {
                            value: b.customTagId,
                            label: b.parentTagName || b.customTagName,
                            tagOptions: b.tagOptions,
                        }
                    )),
                ],
                onChange: (v) => {
                    this.newWidget.changeNewWidgetBulk([{ value: v.value || undefined, field: 'customTag' }])
                    this.newWidget.changeNewWidgetBulk([{
                        value: v.tagOptions.map((b) => {
                            const tagOptionConfig = JSON.parse(b.tagOptionConfig)
                            const label = tagOptionConfig.name || tagOptionConfig.langs.en_GB
                            return (
                                { value: b.tagOptionId, label }
                            )
                        }),
                        field: 'tagOptions',
                    }])
                },
                dontAutoSet: true,
                isMandatory: false,
                show: (store) => {
                    if (!store.benchmarkTags.length) return 0
                    if (store.dashStore.oId === 6) return 0
                    if (!store.newWidget.scope) return 0
                    return 2
                },
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
            } : undefined,
            process.env.USE_CUSTOM_BENCHMARKS === 'true' ? {
                name: 'tagOptions',
                type: 'select',
                label: i18n.t('dynamicDash.wizard.customFiltersLabel'),
                tooltipText: i18n.t('dynamicDash.wizard.customFiltersTooltip'),
                options: (store) => {
                    const { langcode } = store.appStore
                    const customTag = store.benchmarkTags
                        .find((b) => b.customTagId === R.pathOr(undefined, ['customTag'], store.newWidget))
                    return R.pathOr([], ['tagOptions'], customTag)
                        .map((b) => {
                            const parsedTagConfig = JSON.parse(b.tagOptionConfig)
                            const tagName = R.pathOr(
                                R.pathOr(
                                    R.pathOr('', ['langs', 'en_GB'], parsedTagConfig),
                                    ['langs', langcode],
                                    parsedTagConfig,
                                ),
                                ['name'], parsedTagConfig,
                            )
                            return { value: b.tagOptionId, label: tagName }
                        })
                },
                isMulti: true,
                onChange: (v) => {
                    this.newWidget.changeNewWidgetBulk([{ value: v.value, field: 'tagOptions' }])
                },
                dontAutoSet: true,
                isMandatory: false,
                show: (store) => {
                    if (!store.newWidget.customTag) return 0
                    if (!store.benchmarkTags.length) return 0
                    if (store.dashStore.oId === 6) return 0
                    if (!store.newWidget.scope) return 0
                    return 2
                },
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
                showNumberSelected: true,
            } : undefined,
        ]
        return (
            <div className="controls">
                <div className="controlBlock">
                    <h4 style={{ marginBottom: '16px' }}>
                        Scope
                    </h4>
                    <StagedForm
                        store={this}
                        controls={controls}
                        valueHolder={this.newWidget}
                    />
                </div>
            </div>
        )
    }

    renderWidgetDisplayOptions = () => {
        const controls = [
            {
                name: 'chartType',
                label: 'Chart type',
                type: 'select',
                options: () => this.getAllowedCharts(),
                onChange: async (v) => {
                    this.newWidget.changeNewWidgetBulk([{ value: v.value, field: 'chartType' }])
                    this.setDefaultWidgetRenderSettings()
                },
                isMandatory: true,
                dontAutoSet: false,
                isMulti: false,
                show: (store) => 2,
                menuPortalTarget: document.body,
                styles: {
                    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
                },
            },
        ]
        return (
            <div style={{ marginTop: '16px' }} className="displayOptionsControlBlock chartType">
                <StagedForm
                    store={this}
                    controls={controls}
                    valueHolder={this.newWidget}
                />
            </div>
        )
    }

    getAllowedCharts = () => {
        const { newWidget, dashStore } = this
        const { getChartScope, oId } = dashStore
        const dataLevel = (newWidget.scope === 'product' ? 'event' : newWidget.scope) === getChartScope(oId) ? 'currentLevel' : 'levelBelow'
        let allowedCharts = []

        if (newWidget.metrics.length === 1) {
            const isKpi = R.pathOr('', [0, 'question', 'type'], newWidget.metrics) === 'KPI'
            const isMatrix = R.pathOr(false, [0, 'question', 'isMatrixKpi', newWidget.metrics])

            const chartPath = isKpi
                ? [
                    isMatrix ? 'LMX' : 'KPI',
                    dataLevel,
                ]
                : [
                    R.pathOr('', [0, 'question', 'questionTypeCode'], newWidget.metrics),
                    newWidget.isNumeric,
                    dataLevel,
                ]

            allowedCharts = [...R.pathOr(
                R.pathOr([], chartPath, this.questionChartOptions),
                [...chartPath, newWidget.kpiType],
                this.questionChartOptions,
            )]
        } else if (newWidget.metrics.length > 1) {
            const chartPath = ['multiQuestion', dataLevel]

            allowedCharts = [...R.pathOr([], chartPath, this.questionChartOptions)]
            if ([2, 3].includes(newWidget.metrics.length)) {
                allowedCharts.push('quadrantAnalysis')
            }
        } else if (!['KPI', 'multiQuestion'].includes(newWidget.nonQuestionType) && newWidget.kpiType === 'nps') {
            allowedCharts = [...R.pathOr(
                [],
                [newWidget.questionTypeCode, newWidget.kpiType, dataLevel],
                this.questionChartOptions,
            )]
        } else {
            const chartPath = ['KPI', 'multiQuestion'].includes(newWidget.nonQuestionType)
                ? [newWidget.nonQuestionKpiType === 'LMX' || newWidget.isMatrixKpi ? 'LMX' : newWidget.nonQuestionType, dataLevel]
                : [newWidget.questionTypeCode, newWidget.isNumeric, dataLevel]

            allowedCharts = ['KPI', 'multiQuestion'].includes(newWidget.nonQuestionType)
                ? [...R.pathOr([], chartPath, this.questionChartOptions)]
                : [...R.pathOr(
                    R.pathOr([], chartPath, this.questionChartOptions),
                    [...chartPath, newWidget.kpiType],
                    this.questionChartOptions,
                )]
        }

        return Array.isArray(allowedCharts) ? allowedCharts.map((v) => (this.chartTypes[v])) : []
    }

    setDefaultWidgetRenderSettings = () => {
        const { dashStore, newWidget } = this
        const {
            chartType, isCurrency, kpiType, scope,
        } = newWidget
        const pctFormat = kpiType === 'pct' ? '%' : 'numeric'
        const widgetRenderSettings = {
            showLegend: true,
            showDataLabels: true,
            labelPosition: ['pie', 'donut'].includes(chartType) ? 'outside' : 'top',
            showOptionLabels: false,
            rotateXAxisLabels: false,
            orientation: 'horizontal',
            swapSeriesXAxis: false,
            format: isCurrency ? 'currency' : pctFormat,
            sortBy: scope === 'product' && dashStore.oId !== 6 ? 'endDate' : 'none',
            sortDirection: 1,
            removeZeroNull: 'dontRemove',
            dataDecimals: ['text', 'gauge'].includes(chartType) || kpiType === 'avg' ? 2 : 0,
            showTitle: false,
            showYAxis: true,
            scrollable: false,
        }

        newWidget.changeNewWidgetBulk([{
            value: widgetRenderSettings,
            field: 'widgetRenderSettings',
        }])
    }

    getMetricQuestions = (metric) => {
        const { oId } = this.dashStore
        const productType = R.pathOr(metric.productType, ['productType', 'value'], metric)
        const surveyType = R.pathOr(metric.surveyType, ['surveyType', 'value'], metric)
        const industry = R.pathOr(metric.industry, ['industry', 'value'], metric)
        const respondentType = R.pathOr(metric.respondentType, ['respondentType', 'value'], metric)
        const isEventQuestion = oId === 6

        return this.detailedQuestionsMap.get(`${industry}-${productType}-${surveyType}-${respondentType}-${isEventQuestion}`) || []
    }

    toggleV3WidgetEdit = async (chartKey) => {
        const { widgets, appStore } = this.dashStore
        const editingWidget = widgets.find(({ key }) => key === chartKey)
        editingWidget.editing = true
        editingWidget.previousKey = chartKey
        this.newWidget = new NewWidget(editingWidget, this.dashStore, appStore)
        this.toggleShowWidgetCreatorV3()
        await Promise.all(this.newWidget.metrics.map((m) => this.getQuestions(m)))
        const kpiOptions = this.getAllowedKpis()
        this.newWidget.metrics.forEach((metric) => {
            const questions = [...this.getMetricQuestions(metric), ...kpiOptions.filter((k) => (
                k.respondentType === R.pathOr(metric.respondentType, ['respondentType', 'value'], metric)
                        && k.industry === R.pathOr(metric.industry, ['industry', 'value'], metric)
                        && k.surveyType === R.pathOr(metric.surveyType, ['surveyType', 'value'], metric)
                        && k.productType === R.pathOr(metric.productType, ['productType', 'value'], metric)
            )).map((k) => (
                {
                    type: 'KPI',
                    label: k.name,
                    value: k.kpiId,
                    isMatrixKpi: !!k.settings.quadrantNames || k.kpiId === 'LMX',
                    matrixAxisNames: k.settings.quadrantNames ? Object.fromEntries(k.variables.map(({ name }) => name.split('-'))) : undefined,
                }
            ))]

            const question = questions.find((q) => q.value === metric.questionId)
            metric.changeNewWidgetMetricBulk([
                { value: question, field: 'question' },
                { value: question.label, field: 'metricLabel' },
            ])
            this.newWidget.setIsNumeric(question, this.dashStore.oId)
            this.newWidget.changeNewWidgetBulk([
                { value: this.newWidget.chartType, field: 'chartType' },
            ])
        })
    }
}

export default WidgetCreatorStore
